/** Voluntary "Cambia la mia password" for any signed-in admin user (uses POST /v1/auth/change-password). */
import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Loader2, KeyRound } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/sonner';
import { changePassword } from '@/services/auth/authService';
import { formatApiError } from '@/services/api/client';

const EMPTY = { current: '', next: '', confirm: '' };

function PwdField({ label, value, onChange, testId, autoComplete }) {
	return (
		<div>
			<Label className="text-xs text-slate-400">{label}</Label>
			<Input data-testid={testId} type="password" value={value} autoComplete={autoComplete}
				onChange={(e) => onChange(e.target.value)}
				className="mt-1 border-slate-700 bg-slate-900 text-slate-100" />
		</div>
	);
}

function validate(f) {
	if (!f.current) return 'Inserisci la password attuale.';
	if (f.next.length < 8) return 'La nuova password deve avere almeno 8 caratteri.';
	if (f.next === f.current) return 'La nuova password deve essere diversa da quella attuale.';
	if (f.next !== f.confirm) return 'Le due nuove password non coincidono.';
	return null;
}

export function ChangeMyPasswordDialog({ open, onOpenChange, onChanged }) {
	const [f, setF] = useState(EMPTY);
	const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));
	const error = validate(f);
	const close = (o) => { if (!o) setF(EMPTY); onOpenChange(o); };

	const mut = useMutation({
		mutationFn: () => changePassword({ current_password: f.current, new_password: f.next }),
		onSuccess: async () => {
			setF(EMPTY);
			toast.success('Password aggiornata. Accedi di nuovo con la nuova password.');
			await onChanged();
		},
		onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
	});

	return (
		<Dialog open={open} onOpenChange={close}>
			<DialogContent data-testid="change-my-password-dialog" className="border-slate-800 bg-slate-950 text-slate-100">
				<DialogHeader>
					<DialogTitle className="flex items-center gap-2"><KeyRound size={18} className="text-emerald-300" /> Cambia la mia password</DialogTitle>
					<DialogDescription className="text-slate-400">
						Dopo il cambio tutte le sessioni aperte verranno chiuse e dovrai accedere di nuovo.
					</DialogDescription>
				</DialogHeader>
				<form className="space-y-4" onSubmit={(e) => { e.preventDefault(); if (!error) mut.mutate(); }}>
					<PwdField label="Password attuale" value={f.current} onChange={set('current')} testId="change-my-password-current-input" autoComplete="current-password" />
					<PwdField label="Nuova password (min 8 caratteri)" value={f.next} onChange={set('next')} testId="change-my-password-new-input" autoComplete="new-password" />
					<PwdField label="Ripeti la nuova password" value={f.confirm} onChange={set('confirm')} testId="change-my-password-confirm-input" autoComplete="new-password" />
					{error && (f.next || f.confirm) && <p data-testid="change-my-password-validation" className="text-xs text-amber-300">{error}</p>}
					<DialogFooter>
						<Button type="submit" data-testid="change-my-password-submit" disabled={mut.isPending || !!error}
							className="bg-emerald-400 text-slate-950 hover:bg-emerald-300">
							{mut.isPending && <Loader2 size={15} className="mr-1 animate-spin" />} Aggiorna password
						</Button>
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>
	);
}
